import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { InputProps } from 'components/gruenland/atoms/input';
import PasswordInput from 'components/gruenland/atoms/input-password';
import { useFormat } from 'helpers/hooks/useFormat';
import { validatePassword } from 'helpers/validation';
import { Account } from 'types/entity/account';
import AccountForm from '../../../account-atoms/account-form';
import useDiscardForm from '../../../hooks/useDiscardForm';

type ChangePasswordFormData = {
  password: string;
  newPassword: string;
  confirmPassword: string;
};

interface Props {
  changePassword?: (oldPassword: string, newPassword: string) => Promise<Account>;
}

const ChangePasswordForm = ({ changePassword }: Props) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });
  const { discardForm } = useDiscardForm();

  const defaultData: ChangePasswordFormData = { password: '', newPassword: '', confirmPassword: '' };
  const [data, setData] = useState<ChangePasswordFormData>(defaultData);
  const [loading, setLoading] = useState(false);

  const newPasswordIsNotValidMessage = formatMessage({
    id: 'validation.password',
    defaultMessage: 'La password deve essere lunga almeno 8 caratteri e contenere almeno una lettera maiuscola.',
  });

  const confirmPasswordIsNotValidMessage = formatMessage({
    id: 'validation.confirmPassword',
    defaultMessage: 'Le password non corrispondono.',
  });

  const confirmPasswordErrorMessage = useMemo(
    () => (data.confirmPassword.length > 0 ? confirmPasswordIsNotValidMessage : newPasswordIsNotValidMessage),
    [confirmPasswordIsNotValidMessage, data.confirmPassword.length, newPasswordIsNotValidMessage],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    // Generating a toast in case of errors
    if (!validatePassword(data.newPassword)) {
      toast.error(newPasswordIsNotValidMessage);
    } else if (data.newPassword !== data.confirmPassword) {
      toast.error(confirmPasswordErrorMessage);
    } else {
      setLoading(true);
      // Request update password
      changePassword?.(data.password, data.newPassword).then((account) => {
        if (account.accountId) {
          toast.success(formatMessage({ id: 'feedback.dataUpdated', defaultMessage: 'Dati aggiornati con successo.' }));
          setLoading(false);
          discardForm();
        } else {
          setLoading(false);
          toast.error(
            formatMessage({
              id: 'feedback.wentWrong',
              defaultMessage: 'Qualcosa è andato storto. Riprova più tardi.',
            }),
          );
        }
      });
    }
  };

  const inputFields: Array<InputProps> = [
    { label: formatMessage({ id: 'user.currentPassword', defaultMessage: 'Password attuale' }), name: 'password' },
    {
      label: formatMessage({ id: 'user.newPassword', defaultMessage: 'Nuova password' }),
      name: 'newPassword',
      errorMessage: newPasswordIsNotValidMessage,
      validation: validatePassword,
    },
    {
      label: formatMessage({ id: 'user.confirmPassword', defaultMessage: 'Conferma password' }),
      name: 'confirmPassword',
      errorMessage: confirmPasswordErrorMessage,
      validation: (password: string) => password === data.newPassword && !!password.length,
    },
  ];

  return (
    <AccountForm
      title={formatMessage({ id: 'account.changePassword', defaultMessage: 'Cambia la tua password' })}
      requiredLabelIsVisible
      defaultCTASection
      loading={loading}
      onSubmit={handleSubmit}
    >
      <div className="grid gap-3">
        {inputFields.map((fieldProps, index) => (
          <PasswordInput
            key={index}
            {...fieldProps}
            onChange={handleChange}
            value={data[fieldProps.name as keyof ChangePasswordFormData]}
            required
          />
        ))}
      </div>
    </AccountForm>
  );
};

export default ChangePasswordForm;
